/**
 * FinalResponseCard Component
 *
 * Displays the aggregated final response from all specialist agents
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

interface FinalResponseCardProps {
  response: string;
  metadata?: {
    total_agents: number;
    successful_agents: number;
    failed_agents: number;
    success_rate: number;
  };
}

const FinalResponseCard: React.FC<FinalResponseCardProps> = ({ response, metadata }) => {
  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border-2 border-blue-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-blue-200">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-2xl">
            {'\uD83E\uDD16'}
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">Super Agent 総合レポート</h3>
            <p className="text-sm text-gray-600">複数の専門エージェントによる統合分析結果</p>
          </div>
        </div>
        {metadata && (
          <div className="text-right">
            <div className="text-3xl font-bold text-blue-600">
              {Math.round(metadata.success_rate)}%
            </div>
            <div className="text-xs text-gray-600">成功率</div>
          </div>
        )}
      </div>

      {/* Response Content */}
      <div className="bg-white rounded-xl p-4 mb-4">
        <div className="prose prose-sm max-w-none prose-headings:font-bold prose-headings:text-gray-900 prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-p:text-base prose-li:text-base">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              h1: ({ children }) => (
                <h1 className="font-bold text-2xl mt-4 mb-3 text-gray-900">
                  {children}
                </h1>
              ),
              h2: ({ children }) => (
                <h2 className="font-bold text-xl mt-4 mb-2 text-gray-900">
                  {children}
                </h2>
              ),
              h3: ({ children }) => (
                <h3 className="font-semibold text-lg mt-3 mb-2 text-gray-900">
                  {children}
                </h3>
              ),
              p: ({ children }) => (
                <p className="mb-3 text-gray-900">
                  {children}
                </p>
              ),
              ul: ({ children }) => (
                <ul className="ml-4 mb-3 space-y-1 list-disc">
                  {children}
                </ul>
              ),
              ol: ({ children }) => (
                <ol className="ml-4 mb-3 space-y-1 list-decimal">
                  {children}
                </ol>
              ),
              li: ({ children }) => (
                <li className="text-gray-900">
                  {children}
                </li>
              ),
              strong: ({ children }) => (
                <strong className="font-semibold text-gray-900">
                  {children}
                </strong>
              ),
              code: ({ children, className }) => {
                const isInline = !className;
                return isInline ? (
                  <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono text-gray-800">
                    {children}
                  </code>
                ) : (
                  <code className={className}>
                    {children}
                  </code>
                );
              },
              pre: ({ children }) => (
                <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto mb-3">
                  {children}
                </pre>
              ),
              blockquote: ({ children }) => (
                <blockquote className="border-l-4 border-blue-400 pl-4 italic text-gray-700 mb-3">
                  {children}
                </blockquote>
              ),
              table: ({ children }) => (
                <div className="overflow-x-auto mb-3">
                  <table className="border-collapse border border-gray-300 w-full">
                    {children}
                  </table>
                </div>
              ),
              thead: ({ children }) => (
                <thead className="bg-gray-50">
                  {children}
                </thead>
              ),
              th: ({ children }) => (
                <th className="px-4 py-2 text-left font-semibold text-gray-900 border border-gray-300">
                  {children}
                </th>
              ),
              td: ({ children }) => (
                <td className="px-4 py-2 text-gray-700 border border-gray-300">
                  {children}
                </td>
              ),
              a: ({ href, children }) => (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  {children}
                </a>
              )
            }}
          >
            {response}
          </ReactMarkdown>
        </div>
      </div>

      {/* Metadata Stats */}
      {metadata && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-blue-600">{metadata.total_agents}</div>
            <div className="text-xs text-gray-600">参加エージェント</div>
          </div>
          <div className="bg-white rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-green-600">{metadata.successful_agents}</div>
            <div className="text-xs text-gray-600">成功</div>
          </div>
          <div className="bg-white rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-red-600">{metadata.failed_agents}</div>
            <div className="text-xs text-gray-600">失敗</div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-4 flex items-center space-x-2">
        <button
          className="px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
          onClick={() => navigator.clipboard.writeText(response)}
        >
          コピー
        </button>
        <button
          className="px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
          onClick={() => window.print()}
        >
          印刷
        </button>
      </div>
    </div>
  );
};

export default FinalResponseCard;
